import {
  DestroyRef,
  Directive,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  afterEveryRender,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltip, TooltipPosition } from '@angular/material/tooltip';
import { timer } from 'rxjs';
import { getTooltipArrowShift } from './tooltip-arrow-shift';

const STAY_VISIBLE_TIME = 1500;

export enum TooltipType {
  Info = 'info',
  Success = 'success',
  Error = 'error',
}

const mapTooltipClass = {
  [TooltipType.Info]: 'tooltip-info',
  [TooltipType.Success]: 'tooltip-success',
  [TooltipType.Error]: 'tooltip-error',
};
@Directive({
  selector: '[uiTooltip]',
  hostDirectives: [MatTooltip],
  exportAs: 'uiTooltip',
})
export class TooltipDirective implements OnDestroy {
  @Input() clickMessage = '';
  @Input() actionType!: TooltipType;
  @Input() isMobile = false;
  @Input() tooltipClass = '';

  @Input({ transform: (value: boolean | '') => value === '' || !!value })
  @HostBinding('class.ui-tooltip-underline')
  showUnderline = false;

  @Input() set position(value: TooltipPosition) {
    this.tooltip.position = value;
  }

  // Not named `disabled`: the directive attaches to arbitrary hosts, several of which
  // already bind [disabled] for their own purposes.
  @Input() set tooltipDisabled(value: boolean) {
    this.tooltip.disabled = value;
  }

  @Input() set uiTooltip(value: string) {
    this.originalMessage = value;
    this.tooltip.message = value;
    this.setTooltipDefaultSettings();
  }

  show() {
    this.tooltip.show();
  }

  private destroyRef = inject(DestroyRef);
  private elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private originalMessage = '';
  private tooltip = inject(MatTooltip);

  constructor() {
    // The read phase runs after the CDK repositions the overlay in mixedReadWrite, so the panel
    // is measured where it is about to paint. An earlier phase would see the previous position.
    afterEveryRender({ read: () => this.alignArrowToTrigger() });
  }

  @HostListener('click')
  onClick(): void {
    if (!this.clickMessage || !this.actionType) {
      if (this.isMobile) {
        // For mobile, mat-tooltip only shows on long press by default
        this.tooltip.show();
      }
      return;
    }

    this.showTooltipWithStatusChange(this.actionType);

    timer(STAY_VISIBLE_TIME)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.isMobile) {
          this.tooltip.hide();
          return;
        }
        this.resetAndUpdateMessage();
      });
  }

  private resetAndUpdateMessage(): void {
    this.updateTooltip(
      this.originalMessage,
      replaceTooltipClass(getTooltipClasses(this.tooltip.tooltipClass), TooltipType.Info)
    );
    this.hide();
  }

  showTooltipWithStatusChange(type: TooltipType): void {
    this.tooltip.show();
    this.updateTooltip(
      this.clickMessage,
      replaceTooltipClass(getTooltipClasses(this.tooltip.tooltipClass), type)
    );
    this.hide(STAY_VISIBLE_TIME);
  }

  private updateTooltip(message: string, tooltipClass: string[]): void {
    this.tooltip.message = message ?? '';
    this.tooltip.tooltipClass = tooltipClass;
  }

  private hide(delay = 0) {
    this.tooltip.hideDelay = delay;
    this.tooltip.hide();
  }

  // The CDK does not report how far it pushed the panel back into the viewport, and
  // positionChanges only fires when the side changes, so the offset has to be measured.
  private alignArrowToTrigger(): void {
    const panel = this.tooltip._tooltipInstance?.isVisible()
      ? this.tooltip._overlayRef?.overlayElement
      : undefined;

    if (!panel) {
      return;
    }

    const shift = getTooltipArrowShift(
      this.elementRef.nativeElement.getBoundingClientRect(),
      panel.getBoundingClientRect()
    );
    panel.style.setProperty('--ui-tooltip-arrow-shift-x', `${shift.x}px`);
    panel.style.setProperty('--ui-tooltip-arrow-shift-y', `${shift.y}px`);
  }

  private setTooltipDefaultSettings() {
    this.tooltip.tooltipClass = [
      'ui-tooltip',
      mapTooltipClass[TooltipType.Info],
      this.tooltipClass,
    ];
    this.tooltip.showDelay = 50;
    this.tooltip.touchGestures = 'on';
  }

  ngOnDestroy(): void {
    this.hide();
  }
}
const getTooltipClasses = (classes: MatTooltip['tooltipClass']): string[] =>
  Array.isArray(classes) ? classes : [];

const replaceTooltipClass = (tooltipClass: string[], type: TooltipType): string[] => [
  ...removeTooltipClass(tooltipClass),
  mapTooltipClass[type],
];
const removeTooltipClass = (tooltipClass: string[]) =>
  tooltipClass.filter(
    (className) =>
      !Object.values(mapTooltipClass).some((tooltipClass) => tooltipClass === className)
  );
